/**
 * VeriFactu API Client (ES, RD 1007/2023 + Orden HAC/1177/2024).
 *
 * Implements `VerifactuApiClient` (invoice-frontend) on top of the shared
 * invoice-outbound plugin router, which this backend mounts under
 * `invoiceOutbound`:
 *
 * - `invoiceOutbound.verifactu.*`         — what the running system did
 *   (declaración responsable, failed queue, retry, pending registros).
 * - `invoiceOutbound.verifactuSettings.*` — the organisation's activation
 *   (art. 2.c Orden) including the art. 16.5 year binding.
 *
 * Everything goes through the typed `@/trpc` client, so a rename in the
 * backend router breaks the build here instead of at runtime.
 */

import type {
  VerifactuAbgleichErgebnis,
  VerifactuActivation,
  VerifactuApiClient,
  VerifactuChainExportRange,
  VerifactuDeclaracion,
  VerifactuSubmission,
} from '@xhubio-saas/invoice-frontend/plugin-invoice'
import { useMemo, useRef } from 'react'
import { trpc } from '@/trpc'

/**
 * Das Ausgabedokument des Ketten-Exports — **abgeleitet**, nicht abgeschrieben.
 *
 * 🔴 invoice-frontend 3.41.0 definiert `VerifactuChainExport`, re-exportiert
 * den Typ aus `plugin-invoice/index.ts` aber NICHT — ein direkter Import
 * kompiliert also nicht. Ihn hier von Hand nachzubauen waere „Liste neben
 * Liste": die Kopie waere beim Anlegen richtig und liefe danach still
 * auseinander. Der Typ wird deshalb aus dem Vertrag des Pakets selbst gezogen;
 * aendert das Paket seine Form, aendert sich diese Zeile mit.
 */
type VerifactuChainExport = Awaited<ReturnType<NonNullable<VerifactuApiClient['exportChain']>>>

/** Queue states the badge knows; anything unexpected is shown as PENDING. */
const SUBMISSION_STATUSES = [
  'PENDING',
  'PROCESSING',
  'ACCEPTED',
  'REJECTED',
  'GIVEN_UP',
] as const satisfies readonly VerifactuSubmission['status'][]

/**
 * Narrow the router's `status: string` to the badge union.
 *
 * The queue also carries `SUBSANACION` (AEAT stored the registro but flagged
 * errors). That state has no badge of its own yet, and it is *not* a success —
 * so it is surfaced as PENDING, i.e. "still needs attention", never as
 * ACCEPTED. A cast would silently paint it green.
 */
function toSubmissionStatus(status: string): VerifactuSubmission['status'] {
  return SUBMISSION_STATUSES.find((known) => known === status) ?? 'PENDING'
}

export function useVerifactuClient(): VerifactuApiClient {
  const utils = trpc.useUtils()
  // Eine Absage, ein Sprecher (Plan API 09-26/09 S3c): das VeriFactu-Plugin meldet Kontext und Grund
  // selbst (invoice-frontend 3.45.1, `retry.error` + `mapErrorToMessage`) — der Kernel-Toast schweigt.
  const retryMutation = trpc.invoiceOutbound.verifactu.retrySubmission.useMutation({
    meta: { suppressGlobalErrorToast: true },
  })
  // Eine Absage, ein Sprecher (Plan API 09-26/09 S2): das VeriFactu-Plugin meldet die Absage selbst,
  // mit Kontext und Grund (`invoice.verifactu.activation.saveError` + Grund) — der Kernel-Toast schweigt.
  const setEnabledMutation = trpc.invoiceOutbound.verifactuSettings.setEnabled.useMutation({
    meta: { suppressGlobalErrorToast: true },
  })

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    retryMutation,
    setEnabledMutation,
  })
  mutations.current = {
    retryMutation,
    setEnabledMutation,
  }

  return useMemo(
    () => ({
      async getDeclaracion(): Promise<VerifactuDeclaracion> {
        await utils.invoiceOutbound.verifactu.getDeclaracionResponsable.invalidate()
        return utils.invoiceOutbound.verifactu.getDeclaracionResponsable.fetch()
      },

      /**
       * Die Registros dieser Kette als EIN Dokument (Art. 14 RD 1007/2023,
       * Plan API 09-03/02 · Schritt 7).
       *
       * 🔴 Bewusst `invalidate()` davor: eine Herausgabe an einen Pruefer,
       * die aus dem Zwischenspeicher kommt, waere der Stand von irgendwann —
       * und das sieht man ihr nicht an. Der Export traegt sein eigenes
       * `exportedAt`; das darf nicht luegen.
       *
       * 🔴 Kein Umformen der Antwort. `registro` ist das EINGEFRORENE
       * Registro, aus dem ein Empfaenger die `huella` nachrechnet — die
       * Rueckgabe des Routers ist strukturgleich zum Vertrag des Pakets,
       * deshalb steht an dieser Grenze auch kein Cast. Jede Zurechtlegung hier
       * waere eine zweite Wahrheit ueber ein Dokument, dessen ganzer Wert die
       * Unveraendertheit ist.
       *
       * 🔴 Der Zeitraum Von/Bis (invoice-frontend 3.67.0) reist mit. Ohne ihn
       * kaeme trotz sichtbarer Felder die ganze Kette heraus — ueber 5000
       * Eintraegen die Absage `CHAIN_EXPORT_RANGE_REQUIRED`. Nur gesetzte
       * Grenzen gehen mit; der Server fuellt das Fehlende selbst.
       */
      async exportChain(
        vatId: string,
        range?: VerifactuChainExportRange
      ): Promise<VerifactuChainExport> {
        await utils.invoiceOutbound.verifactu.exportChain.invalidate()
        return utils.invoiceOutbound.verifactu.exportChain.fetch({ vatId, ...range })
      },

      /**
       * Der Abgleich gegen die AEAT (`ConsultaFactuSistemaFacturacion`,
       * Plan API 09-03/04).
       *
       * 🔴 Die Eingabe wird NICHT hier zusammengebaut: `nif` und
       * `nombreRazon` reicht die Seite durch, und sie bekommt sie aus
       * `organization.current` — beides Werte, die der Server fuehrt. Der NIF
       * wird insbesondere nicht aus dem `vatId` geschnitten; das waere eine
       * Herleitung im Browser fuer einen Wert, den der Server kennt.
       *
       * 🔴 `invalidate()` davor: die Antwort ist der Stand der Behoerde in
       * diesem Augenblick. Eine aus dem Zwischenspeicher beantwortete Abfrage
       * wuerde einen alten Abgleich als neuen ausgeben — und ein Befund
       * „fehlt bei der AEAT", der laengst behoben ist, kostet den Anwender
       * genau das Vertrauen, das diese Flaeche braucht.
       *
       * 🔴 Kein `catch`, kein Umformen: „kein Gateway", „kein Zertifikat" und
       * „AEAT hat abgelehnt" stehen absichtlich IM Ergebnis
       * (`consulta-lauf.service.ts`). Wer sie hier zu einem Fehler
       * zusammenfaltet, wirft die Unterscheidung weg, um die es dort ging.
       */
      async consultarRegistros(args: {
        vatId: string
        nif: string
        nombreRazon: string
        ejercicio: string
        periodo: string
      }): Promise<VerifactuAbgleichErgebnis> {
        await utils.invoiceOutbound.verifactu.consultarRegistros.invalidate()
        return utils.invoiceOutbound.verifactu.consultarRegistros.fetch(args)
      },

      async listFailedSubmissions(): Promise<VerifactuSubmission[]> {
        await utils.invoiceOutbound.verifactu.listFailedSubmissions.invalidate()
        const rows = await utils.invoiceOutbound.verifactu.listFailedSubmissions.fetch(undefined)
        return rows.map((row) => ({ ...row, status: toSubmissionStatus(row.status) }))
      },

      async retrySubmission(submissionId: string): Promise<void> {
        await mutations.current.retryMutation.mutateAsync({ submissionId })
        await utils.invoiceOutbound.verifactu.listFailedSubmissions.invalidate()
        await utils.invoiceOutbound.verifactu.pendingCount.invalidate()
      },

      async getPendingCount(): Promise<number> {
        const result = await utils.invoiceOutbound.verifactu.pendingCount.fetch()
        return result.pendingCount
      },

      async getActivation(): Promise<VerifactuActivation> {
        await utils.invoiceOutbound.verifactuSettings.get.invalidate()
        return utils.invoiceOutbound.verifactuSettings.get.fetch()
      },

      async setActivation(enabled: boolean): Promise<VerifactuActivation> {
        const result = await mutations.current.setEnabledMutation.mutateAsync({ enabled })
        await utils.invoiceOutbound.verifactuSettings.get.invalidate()
        return result
      },
    }),
    // NUR `utils`. Keine Mutationen — sonst ist der Client pro Render neu.
    [utils]
  )
}
